import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Clock, Square } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_MS = 30000;       // server refresh
const TICK_MS = 10000;       // local "now" tick — keeps elapsed-minutes display fresh

const formatElapsed = (start) => {
  if (!start) return '—';
  const ms = Date.now() - new Date(start).getTime();
  if (ms < 0) return '0m';
  const totalMins = Math.floor(ms / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const LiveTimerIndicator = ({ user }) => {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState(null);
  const [linkChecked, setLinkChecked] = useState(false);
  const [timers, setTimers] = useState([]);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);

  // Elevated roles see ALL active timers across the organisation.
  const isElevated = ['admin', 'manager', 'superadmin', 'accounts'].includes(user?.role);

  // Resolve linked employee_id once per user
  useEffect(() => {
    let alive = true;
    if (!user?.id) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me/employee`);
        if (alive && r.data?.linked) setEmployeeId(r.data.employee.id);
      } catch {/* ignore */}
      finally { if (alive) setLinkChecked(true); }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const refresh = useCallback(async () => {
    // Wait until we've checked the employee link so we know whether to scope.
    if (!linkChecked) return;
    // For non-elevated users we MUST have an employeeId; otherwise we'd
    // see other employees' timers, which is a privacy leak.
    if (!isElevated && !employeeId) { setTimers([]); return; }
    try {
      const params = (!isElevated && employeeId) ? { employee_id: employeeId } : {};
      const r = await axios.get(`${API}/timer/active`, { params });
      setTimers(r.data || []);
    } catch {/* ignore */}
  }, [employeeId, linkChecked, isElevated]);

  useEffect(() => {
    if (!linkChecked) return undefined;
    refresh();
    pollRef.current = setInterval(refresh, POLL_MS);
    // Listen for app-wide timer changes so the indicator updates the
    // instant a timer is started/stopped from anywhere (e.g. inside a
    // task's Time Entries dialog) without waiting for the next poll.
    const onChange = () => refresh();
    window.addEventListener('timer:changed', onChange);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener('timer:changed', onChange);
    };
  }, [linkChecked, refresh]);

  // Re-tick every 10s for elapsed-minute precision
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(i);
  }, []);

  const stopTimer = async (tItem) => {
    try {
      await axios.post(`${API}/timer/stop`, { task_id: tItem.task_id, employee_id: tItem.employee_id });
      toast.success(`Stopped: ${tItem.task_name || 'task'}`);
      window.dispatchEvent(new CustomEvent('timer:changed'));
      refresh();
    } catch {
      toast.error(t('auto.misc2.failedToStopTimer'));
    }
  };

  // Hide entirely for users who have neither an employee link nor an
  // elevated role — they can never see anything useful here.
  if (linkChecked && !employeeId && !isElevated) return null;

  const count = timers.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`relative ${count > 0 ? 'border-[#AA0000] text-[#AA0000]' : ''}`}
          data-testid="live-timer-indicator"
          aria-label={count > 0 ? `${count} live timer${count > 1 ? 's' : ''} running` : 'No live timers'}
        >
          <Clock className={`h-4 w-4 ${count > 0 ? 'animate-pulse' : ''}`} />
          {count > 0 && (
            <>
              <span className="ml-1.5 tabular-nums font-semibold">{count}</span>
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[#AA0000] animate-pulse ring-2 ring-white" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="live-timer-popover">
        <div className="p-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#AA0000]" />
            <span className="font-semibold text-sm">{t('auto.misc2.liveTimers')}</span>
            {count > 0 && (
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-[#AA0000] text-white tabular-nums">{count}</span>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto" key={now}>
          {count === 0 && (
            <div className="p-6 text-center text-sm text-gray-500">
              {isElevated ? t('auto.misc2.noTimersRunningAcrossTheTeam') : t('auto.misc2.noTimersRunning')}
              <div className="text-xs text-gray-400 mt-1">{t('auto.misc2.startOneFromAProjectS')}</div>
            </div>
          )}
          {timers.map((tItem) => (
            <div key={tItem.id} className="p-3 border-b last:border-b-0 hover:bg-gray-50">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{tItem.task_name || '(unnamed task)'}</div>
                  <div className="text-xs text-gray-500 truncate">{tItem.project_name || ''}</div>
                  {isElevated && tItem.employee_name && (
                    <div className="text-xs text-gray-600 truncate">👤 {tItem.employee_name}</div>
                  )}
                  <div className="text-xs text-[#AA0000] mt-1 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#AA0000] animate-pulse inline-block" />
                    Running · {formatElapsed(tItem.start_at)}
                    {tItem.task_category === 'extra' && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px]">{t('auto.misc2.extra')}</span>
                    )}
                    {tItem.task_category === 'rework' && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px]">{t('auto.misc2.rework')}</span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => stopTimer(tItem)}
                  className="text-[#AA0000] hover:text-white hover:bg-[#AA0000] border-[#AA0000]"
                  data-testid={`stop-timer-${tItem.task_id}`}
                >
                  <Square className="h-3 w-3 mr-1" />{t('auto.misc2.stop')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LiveTimerIndicator;
